import React, { useState, useEffect } from 'react';
import { collection, addDoc, writeBatch, getDocs, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { COLLECTIONS } from '../lib/firebase/collections';
import type { CardCategory } from '../types/game';

const JsonImporter: React.FC = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [existingCategoryId, setExistingCategoryId] = useState('');
  const [categories, setCategories] = useState<CardCategory[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, COLLECTIONS.CARD_CATEGORIES));
        setCategories(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CardCategory)));
      } catch (err) {
        console.error("Fehler beim Laden der Kategorien:", err);
        setError("Kategorien konnten nicht geladen werden.");
      }
    };
    fetchCategories();
  }, []);

  const handleImport = async () => {
    if (!jsonInput.trim()) return alert("Bitte JSON einfügen.");
    if (!newCategoryName.trim() && !existingCategoryId) return alert("Bitte eine neue Kategorie benennen oder eine bestehende auswählen.");

    setIsImporting(true);
    setError('');
    try {
      const data = JSON.parse(jsonInput);
      const { black_cards, white_cards } = data;
      let categoryId = existingCategoryId;

      if (newCategoryName.trim()) {
        const categoryRef = await addDoc(collection(db, COLLECTIONS.CARD_CATEGORIES), {
          name: newCategoryName.trim(),
          description: "Importiert via Admin Panel",
          isActive: true,
          createdAt: new Date(),
        });
        categoryId = categoryRef.id;
      }

      const batch = writeBatch(db);
      const blackCardCount = black_cards?.length || 0;
      const whiteCardCount = white_cards?.length || 0;

      if (blackCardCount > 0) {
        black_cards.forEach((card: { text: string, pick: number }) => {
          const newCardRef = doc(collection(db, COLLECTIONS.BLACK_CARDS));
          batch.set(newCardRef, { ...card, categoryIds: [categoryId], createdAt: new Date() });
        });
      }

      if (whiteCardCount > 0) {
        white_cards.forEach((text: string) => {
          const newCardRef = doc(collection(db, COLLECTIONS.WHITE_CARDS));
          batch.set(newCardRef, { text, categoryIds: [categoryId], createdAt: new Date() });
        });
      }

      await batch.commit();
      alert(`Import erfolgreich! ${blackCardCount} schwarze und ${whiteCardCount} weiße Karten wurden hinzugefügt.`);
      setJsonInput('');
      setNewCategoryName('');
      setExistingCategoryId('');
      // Refresh categories list
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.CARD_CATEGORIES));
      setCategories(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CardCategory)));

    } catch (err) {
      console.error("Import-Fehler:", err);
      setError("Fehler beim Importieren. Überprüfe das JSON-Format und die Konsole.");
      alert("Fehler beim Importieren. Überprüfe das JSON-Format und die Konsole.");
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="bg-gray-800 p-6 rounded-lg">
      <h2 className="text-2xl font-bold mb-4 text-green-300">Karten per JSON importieren</h2>
      {error && <p className="text-red-400 bg-red-900/50 p-3 rounded-md mb-4">{error}</p>}
      <textarea
        className="input w-full h-60 mb-4 bg-gray-900"
        placeholder='{ "black_cards": [...], "white_cards": [...] }'
        value={jsonInput}
        onChange={(e) => setJsonInput(e.target.value)}
      />
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <input
          type="text"
          className="input flex-grow"
          placeholder="Name für neue Kategorie"
          value={newCategoryName}
          onChange={(e) => { setNewCategoryName(e.target.value); setExistingCategoryId(''); }}
        />
        <span className="self-center text-gray-400">ODER</span>
        <select
          className="input flex-grow"
          value={existingCategoryId}
          onChange={(e) => { setExistingCategoryId(e.target.value); setNewCategoryName(''); }}
        >
          <option value="">Zu bestehender hinzufügen</option>
          {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
        </select>
      </div>
      <button onClick={handleImport} disabled={isImporting} className="btn btn-primary w-full py-3 text-lg">
        {isImporting ? 'Importiere...' : 'Karten importieren'}
      </button>
    </div>
  );
};

export default JsonImporter; 